import { isNull, isUndefined } from 'lodash/fp';
import { push } from 'redux-first-history';
import { all, call, fork, put, select, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';

import { fetchAllDatasetsRiskToleranceAnalytics, fetchAllDocumentsRiskToleranceAnalytics, fetchDatasetFieldRiskToleranceAnalytics, fetchDocumentRiskToleranceAnalytics, fetchDocumentSpreadAnalytics, fetchEntityDocumentsRiskToleranceAnalytics, fetchEntityJurisdictionAnalytics, fetchEntitySpreadAnalytics, fetchJurisdictionDocumentsForEntity, fetchPaginatedRiskToleranceDocuments, fetchPreExecutionDocumentsRiskToleranceAnalytics } from '../../../../services/analytics';
import { recalculateClientDocumentRiskTolerance } from '../../../../services/dataset';
import { fetchAllGroupEntities, fetchAllMyCompanyEntities } from '../../../../services/entity';
import { BaseEntity, GroupEntity } from '../../../admin/entity/store/types';
import { RiskToleranceDataset } from '../../../admin/risk-tolerance/store/types';
import { PieChartAnalyticsData } from '../../../shared/analytics/PieChart';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import { ColumnSort, TableFilters } from '../../../shared/table/types';
import { openDocumentAndInstance } from '../../my-documents/store/actions';
import {
    fetchAllDatasetRiskToleranceFailed,
    fetchAllDatasetRiskToleranceStarted,
    fetchAllDatasetRiskToleranceSuccessful,
    fetchAllDocumentsRiskToleranceFailed,
    fetchAllDocumentsRiskToleranceStarted,
    fetchAllDocumentsRiskToleranceSuccessful,
    fetchAllEntityDocumentsRiskToleranceFailed,
    fetchAllEntityDocumentsRiskToleranceStarted,
    fetchAllEntityDocumentsRiskToleranceSuccessful,
    fetchAllMyCompanyEntitiesFailed,
    fetchAllMyCompanyEntitiesSuccessful,
    fetchAllPreExecutionDocumentsRiskToleranceFailed,
    fetchAllPreExecutionDocumentsRiskToleranceStarted,
    fetchAllPreExecutionDocumentsRiskToleranceSuccessful,
    fetchDatasetFieldRiskToleranceFailed,
    fetchDatasetFieldRiskToleranceStarted,
    fetchDatasetFieldRiskToleranceSuccessful,
    fetchDocumentRiskToleranceFailed,
    fetchDocumentRiskToleranceStarted,
    fetchDocumentRiskToleranceSuccessful,
    fetchDocumentSpreadFailed,
    fetchDocumentSpreadStarted,
    fetchDocumentSpreadSuccessful,
    fetchEntityJurisdictionFailed,
    fetchEntityJurisdictionStarted,
    fetchEntityJurisdictionSuccessful,
    fetchEntitySpreadFailed,
    fetchEntitySpreadStarted,
    fetchEntitySpreadSuccessful,
    fetchJurisdictionDocumentsFailed,
    fetchJurisdictionDocumentsStarted,
    fetchJurisdictionDocumentsSuccessful,
    fetchPaginatedDocumentsRiskToleranceFailed,
    fetchPaginatedDocumentsRiskToleranceStarted,
    fetchPaginatedDocumentsRiskToleranceSuccessful,
    recalculateMyDocumentRiskToleranceFailed,
    recalculateMyDocumentRiskToleranceSuccessful,
    redirectToDocumentInstance,
    selectJurisdictionDocumentsPage,
    setDocumentTileInView,
    setEntityDocumentRiskToleranceView,
    setPreExecutionDocumentRiskToleranceView,
    setRiskToleranceView,
    setSelectedMyCompanyEntity
} from './actions';
import { getRiskToleranceDatasetFieldColumnSort, getRiskToleranceDatasetFieldFilters, getRiskToleranceDatasetFieldPageNumber, getRiskToleranceDatasetFieldPageSize, getRiskToleranceDocumentsColumnSort, getRiskToleranceDocumentsFilters, getRiskToleranceDocumentsPageNumber, getRiskToleranceDocumentsPageSize, getSelectedMyCompanyEntity, getSelectedRiskToleranceChildDatasetId, getSelectedRiskToleranceDatasetFieldId, getSelectedRiskToleranceParentDatasetId, getShowOverrideRiskRating, getTileInView } from './selectors';
import { AllDocumentsRiskTolerance, BaseArkDocument, DocumentAnalyticsActionTypes, DocumentAnalyticsChart, DocumentRiskTolerance, DocumentRiskView, EntityDocumentRiskView, EntityJurisdictionAnalytics, PaginatedDatasetFieldRiskTolerance, PreExecutionDocumentRiskView, TableDocumentsRiskTolerance } from './types';

export function* attemptRedirectTileInView({ payload }: ReturnType<typeof setDocumentTileInView>) {
    const { tileInView, shouldRedirect } = payload;
    if (shouldRedirect) {
        const redirectUrl = isNull(tileInView) ? '/documents/analytics' : `/documents/analytics/${tileInView.replaceAll(' ', '-')}`;
        yield put(push(redirectUrl));
    }
}

function* setTileInViewWatcher() {
    yield takeLatest(DocumentAnalyticsActionTypes.SET_DOCUMENT_ANALYTICS_TILE_IN_VIEW, attemptRedirectTileInView);
}

export function* attemptFetchDocumentSpread({ payload }: ReturnType<typeof fetchDocumentSpreadStarted>) {
    try {
        const { entityId } = payload;
        const analytics: PieChartAnalyticsData[] = yield call(fetchDocumentSpreadAnalytics, { entityId });
        yield put(fetchDocumentSpreadSuccessful(analytics));
    } catch (e) {
        yield put(fetchDocumentSpreadFailed((e as Error).message));
    }
}

function* fetchDocumentSpreadWatcher() {
    yield takeLatest(DocumentAnalyticsActionTypes.FETCH_DOCUMENT_SPREAD_ANALYTICS_STARTED, attemptFetchDocumentSpread);
}

export function* attemptFetchEntitySpread({ payload }: ReturnType<typeof fetchEntitySpreadStarted>) {
    try {
        const { entityId } = payload;
        const analytics: PieChartAnalyticsData[] = yield call(fetchEntitySpreadAnalytics, { entityId });
        yield put(fetchEntitySpreadSuccessful(analytics));
    } catch (e) {
        yield put(fetchEntitySpreadFailed((e as Error).message));
    }
}

function* fetchEntitySpreadWatcher() {
    yield takeLatest(DocumentAnalyticsActionTypes.FETCH_ENTITY_SPREAD_ANALYTICS_STARTED, attemptFetchEntitySpread);
}

export function* attemptFetchAvailableAnalyticsEntities() {
    try {
        const entities: BaseEntity[] = yield call(fetchAllMyCompanyEntities);
        const { groupEntities }: { groupEntities: GroupEntity[]; } = yield call(fetchAllGroupEntities, { myCompanyOnly: true });
        let entityOptions: DropdownOption[] = [];
        if (groupEntities.length > 0) {
            groupEntities.map(({ name, entityId }) => entityOptions.push({ label: name, value: entityId!.toString() }));
            entityOptions.push({ value: '', label: '', showDivider: true, disabled: true });
        }
        if (entities.length > 0) {
            entities.map(({ name, entityId }) => entityOptions.push({ label: name, value: entityId.toString() }));
        }
        yield put(fetchAllMyCompanyEntitiesSuccessful(entityOptions));
        const entityId = entities.length ? entities[0].entityId : null;
        yield put(setSelectedMyCompanyEntity(entityId));
    } catch (e) {
        yield put(fetchAllMyCompanyEntitiesFailed((e as Error).message));
    }
}

function* attemptFetchAvailableAnalyticsEntitiesWatcher() {
    yield takeLatest(DocumentAnalyticsActionTypes.FETCH_ALL_MY_COMPANY_ENTITIES_STARTED, attemptFetchAvailableAnalyticsEntities);
}

export function* setSelectedCompanyWorker({ payload }: ReturnType<typeof setSelectedMyCompanyEntity>) {
    yield put(fetchEntitySpreadStarted(payload));
    yield put(fetchDocumentSpreadStarted(payload));
    const tileInView: DocumentAnalyticsChart = yield select(getTileInView);

    if (!isNull(payload)) {
        yield put(fetchAllEntityDocumentsRiskToleranceStarted(payload));
        yield put(fetchEntityJurisdictionStarted(payload));
        if (tileInView === DocumentAnalyticsChart.ENTITY_DOCUMENT_RISK_TOLERANCE) {
            const entityId: number = yield select(getSelectedMyCompanyEntity);
            yield put(fetchPaginatedDocumentsRiskToleranceStarted(1, entityId));
            yield put(fetchAllDatasetRiskToleranceStarted(entityId));
        }
        if (tileInView === DocumentAnalyticsChart.DOCUMENT_RISK_TOLERANCE) {
            yield put(fetchPaginatedDocumentsRiskToleranceStarted(1, undefined));
        }
    } else {
        yield put(fetchEntityJurisdictionSuccessful([]));
    }
}

function* setSelectedCompanyWatcher() {
    yield takeLatest(DocumentAnalyticsActionTypes.SET_SELECTED_MY_COMPANY_ENTITY, setSelectedCompanyWorker);
}

export function* attemptFetchEntityJurisdiction({ payload }: ReturnType<typeof fetchEntityJurisdictionStarted>) {
    try {
        const { entityId } = payload;
        const analytics: EntityJurisdictionAnalytics[] = yield call(fetchEntityJurisdictionAnalytics, { entityId });
        yield put(fetchEntityJurisdictionSuccessful(analytics));
    } catch (e) {
        yield put(fetchEntityJurisdictionFailed((e as Error).message));
    }
}

function* fetchEntityJurisdictionWatcher() {
    yield takeLatest(DocumentAnalyticsActionTypes.FETCH_ENTITY_JURISDICTION_ANALYTICS_STARTED, attemptFetchEntityJurisdiction);
}

export function* attemptFetchJurisdictionDocuments({ payload }: ReturnType<typeof fetchJurisdictionDocumentsStarted>) {
    try {
        const { pageNumber, entityId } = payload;
        const myEntityId: number = yield select(getSelectedMyCompanyEntity);
        const { documents, total }: { documents: BaseArkDocument[]; total: number; } = yield call(fetchJurisdictionDocumentsForEntity, { myEntityId, entityId, pageNumber });
        yield put(fetchJurisdictionDocumentsSuccessful(documents, total, pageNumber));
    } catch (e) {
        yield put(fetchJurisdictionDocumentsFailed((e as Error).message));
    }
}

function* fetchJurisdictionDocumentsWatcher() {
    yield takeLatest(DocumentAnalyticsActionTypes.FETCH_JURISDICTION_DOCUMENTS_STARTED, attemptFetchJurisdictionDocuments);
}

export function* jurisdictionDocumentsSelectPage({ payload }: ReturnType<typeof selectJurisdictionDocumentsPage>) {
    const { pageNumber, entityId } = payload;
    yield put(fetchJurisdictionDocumentsStarted(entityId, pageNumber + 1));
}

function* jurisdictionDocumentsSelectPageWatcher() {
    yield takeLatest(DocumentAnalyticsActionTypes.JURISDICTION_DOCUMENTS_SELECT_PAGE, jurisdictionDocumentsSelectPage);
}

export function* attemptFetchDocumentRiskTolerance({ payload }: ReturnType<typeof fetchDocumentRiskToleranceStarted>) {
    try {
        const { documentId, isPreExecution } = payload;
        const documentRiskTolerance: DocumentRiskTolerance | undefined = yield call(fetchDocumentRiskToleranceAnalytics, { documentId, isPreExecution });
        yield put(fetchDocumentRiskToleranceSuccessful(documentRiskTolerance || null));
    } catch (e) {
        yield put(fetchDocumentRiskToleranceFailed((e as Error).message));
    }
}

function* fetchDocumentRiskToleranceWatcher() {
    yield takeLeading(DocumentAnalyticsActionTypes.FETCH_DOCUMENT_RISK_TOLERANCE_STARTED, attemptFetchDocumentRiskTolerance);
}

export function* attemptFetchAllDocumentsRiskTolerance() {
    try {
        const filters: TableFilters = yield select(getRiskToleranceDocumentsFilters);
        const allDocumentsRiskTolerance: AllDocumentsRiskTolerance = yield call(fetchAllDocumentsRiskToleranceAnalytics, { filters });
        yield put(fetchAllDocumentsRiskToleranceSuccessful(allDocumentsRiskTolerance));
    } catch (e) {
        yield put(fetchAllDocumentsRiskToleranceFailed((e as Error).message));
    }
}

function* fetchAllDocumentsRiskToleranceWatcher() {
    yield takeLeading(DocumentAnalyticsActionTypes.FETCH_ALL_DOCUMENTS_RISK_TOLERANCE_STARTED, attemptFetchAllDocumentsRiskTolerance);
}

export function* attemptFetchAllEntityDocumentsRiskTolerance({ payload }: ReturnType<typeof fetchAllEntityDocumentsRiskToleranceStarted>) {
    try {
        const filters: TableFilters = yield select(getRiskToleranceDocumentsFilters);
        const allEntityDocumentsRiskTolerance: AllDocumentsRiskTolerance = yield call(fetchEntityDocumentsRiskToleranceAnalytics, { entityId: payload, filters });
        yield put(fetchAllEntityDocumentsRiskToleranceSuccessful(allEntityDocumentsRiskTolerance));
    } catch (e) {
        yield put(fetchAllEntityDocumentsRiskToleranceFailed((e as Error).message));
    }
}

function* fetchAllEntityDocumentsRiskToleranceWatcher() {
    yield takeLeading(DocumentAnalyticsActionTypes.FETCH_ENTITY_DOCUMENTS_RISK_TOLERANCE_STARTED, attemptFetchAllEntityDocumentsRiskTolerance);
}

export function* attemptFetchAllPreExecutionDocumentsRiskTolerance() {
    try {
        const filters: TableFilters = yield select(getRiskToleranceDocumentsFilters);
        const preExecutionDocumentsRiskTolerance: AllDocumentsRiskTolerance = yield call(fetchPreExecutionDocumentsRiskToleranceAnalytics, { filters });
        yield put(fetchAllPreExecutionDocumentsRiskToleranceSuccessful(preExecutionDocumentsRiskTolerance));
    } catch (e) {
        yield put(fetchAllPreExecutionDocumentsRiskToleranceFailed((e as Error).message));
    }
}

function* fetchAllPreExecutionDocumentsRiskToleranceWatcher() {
    yield takeLeading(DocumentAnalyticsActionTypes.FETCH_ALL_PRE_EXECUTION_DOCUMENTS_RISK_TOLERANCE_STARTED, attemptFetchAllPreExecutionDocumentsRiskTolerance);
}

export function* attemptFetchAllDatasetsRiskTolerance({ payload }: ReturnType<typeof fetchAllDatasetRiskToleranceStarted>) {
    try {
        const { entityId, isPreExecution } = payload;
        const riskToleranceDatasets: RiskToleranceDataset[] = yield call(fetchAllDatasetsRiskToleranceAnalytics, { entityId, isPreExecution });
        yield put(fetchAllDatasetRiskToleranceSuccessful(riskToleranceDatasets));
    } catch (e) {
        yield put(fetchAllDatasetRiskToleranceFailed((e as Error).message));
    }
}

function* fetchAllRiskToleranceWatcher() {
    yield takeLatest(DocumentAnalyticsActionTypes.FETCH_ALL_DATASETS_RISK_TOLERANCE_STARTED, attemptFetchAllDatasetsRiskTolerance);
}

export function* attemptRedirectToDocumentInstance({ payload }: ReturnType<typeof redirectToDocumentInstance>) {
    const { originalDocumentId, datasetInstanceId } = payload;
    yield put(openDocumentAndInstance(originalDocumentId.toString(), datasetInstanceId.toString()));
    yield put(push(`/documents/analysis/${originalDocumentId}/${datasetInstanceId}`));
}

function* goToDocumentInstance() {
    yield takeLeading(DocumentAnalyticsActionTypes.REDIRECT_TO_DOCUMENT_INSTANCE, attemptRedirectToDocumentInstance);
}

export function* attemptFetchPaginatedDatasetFieldRiskTolerance({ payload }: ReturnType<typeof fetchDatasetFieldRiskToleranceStarted>) {
    try {
        const { pageNumber, entityId, isPreExecution } = payload;
        const parentDatasetId: number | null = yield select(getSelectedRiskToleranceParentDatasetId);
        const childDatasetId: number | null = yield select(getSelectedRiskToleranceChildDatasetId);
        const fieldId: string | null = yield select(getSelectedRiskToleranceDatasetFieldId);
        const pageSize: number = yield select(getRiskToleranceDatasetFieldPageSize);
        const filters: TableFilters = yield select(getRiskToleranceDatasetFieldFilters);
        const columnSort: ColumnSort | undefined = yield select(getRiskToleranceDatasetFieldColumnSort);
        const showOverrideRiskRating: boolean = yield select(getShowOverrideRiskRating);
        if (!isNull(parentDatasetId) && !isNull(fieldId) && !isNull(childDatasetId)) {
            const { documents, total, overallPercentage, overallOverridePercentage }: PaginatedDatasetFieldRiskTolerance = yield call(fetchDatasetFieldRiskToleranceAnalytics, { parentDatasetId, childDatasetId, fieldId, pageNumber, pageSize, filters, columnSort, entityId, isPreExecution, showOverrideRiskRating });
            yield put(fetchDatasetFieldRiskToleranceSuccessful(documents, total, overallPercentage, overallOverridePercentage, pageNumber));
            if (!isUndefined(entityId)) {
                yield put(setEntityDocumentRiskToleranceView(EntityDocumentRiskView.ENTITY_DATASET_FIELD));
            } else if (isPreExecution) {
                yield put(setPreExecutionDocumentRiskToleranceView(PreExecutionDocumentRiskView.PRE_EXECUTION_DATASET_FIELD));
            } else {
                yield put(setRiskToleranceView(DocumentRiskView.DATASET_FIELD));
            }
        }
    } catch (e) {
        yield put(fetchDatasetFieldRiskToleranceFailed((e as Error).message));
    }
}

function* fetchPaginatedDatasetFieldRiskToleranceWatcher() {
    yield takeLeading(DocumentAnalyticsActionTypes.FETCH_DATASET_FIELD_RISK_TOLERANCE_STARTED, attemptFetchPaginatedDatasetFieldRiskTolerance);
}

export function* attemptFetchPaginatedDocumentsRiskTolerance({ payload }: ReturnType<typeof fetchPaginatedDocumentsRiskToleranceStarted>) {
    try {
        const { pageNumber, entityId, isPreExecution } = payload;
        const pageSize: number = yield select(getRiskToleranceDocumentsPageSize);
        const filters: TableFilters = yield select(getRiskToleranceDocumentsFilters);
        const columnSort: ColumnSort | undefined = yield select(getRiskToleranceDocumentsColumnSort);
        const showOverrideRiskRating: boolean = yield select(getShowOverrideRiskRating);
        const { documents, total }: TableDocumentsRiskTolerance = yield call(fetchPaginatedRiskToleranceDocuments, { pageNumber, pageSize, filters, columnSort, entityId, isPreExecution, showOverrideRiskRating });
        yield put(fetchPaginatedDocumentsRiskToleranceSuccessful(documents, total, pageNumber));
    } catch (e) {
        yield put(fetchPaginatedDocumentsRiskToleranceFailed((e as Error).message));
    }
}

function* fetchPaginatedDocumentsRiskToleranceWatcher() {
    yield takeLatest(DocumentAnalyticsActionTypes.FETCH_PAGINATED_DOCUMENTS_RISK_TOLERANCE_STARTED, attemptFetchPaginatedDocumentsRiskTolerance);
}

// Document Table Pagination Sagas

export function* documentRiskTolerancePaginationNext() {
    const pageNumber: number = yield select(getRiskToleranceDocumentsPageNumber);
    const tileInView: DocumentAnalyticsChart = yield select(getTileInView);
    const companyEntityId: number | null = yield select(getSelectedMyCompanyEntity);
    const isPreExecution = tileInView === DocumentAnalyticsChart.PRE_EXECUTION_DOCUMENT_RISK_TOLERANCE;
    const isEntityRiskToleranceView = tileInView === DocumentAnalyticsChart.ENTITY_DOCUMENT_RISK_TOLERANCE;
    const entityId = !isNull(companyEntityId) && isEntityRiskToleranceView ? companyEntityId : undefined;
    yield put(fetchPaginatedDocumentsRiskToleranceStarted(pageNumber + 1, entityId, isPreExecution));
}

function* documentRiskTolerancePaginationNextWatcher() {
    yield takeLatest(DocumentAnalyticsActionTypes.DOCUMENT_RISK_TOLERANCE_PAGINATION_NEXT, documentRiskTolerancePaginationNext);
}

export function* documentRiskTolerancePaginationPrevious() {
    const pageNumber: number = yield select(getRiskToleranceDocumentsPageNumber);
    const tileInView: DocumentAnalyticsChart = yield select(getTileInView);
    const companyEntityId: number | null = yield select(getSelectedMyCompanyEntity);
    const isPreExecution = tileInView === DocumentAnalyticsChart.PRE_EXECUTION_DOCUMENT_RISK_TOLERANCE;
    const isEntityRiskToleranceView = tileInView === DocumentAnalyticsChart.ENTITY_DOCUMENT_RISK_TOLERANCE;
    const entityId = !isNull(companyEntityId) && isEntityRiskToleranceView ? companyEntityId : undefined;
    yield put(fetchPaginatedDocumentsRiskToleranceStarted(pageNumber - 1, entityId, isPreExecution));
}

function* documentRiskTolerancePaginationPreviousWatcher() {
    yield takeLatest(DocumentAnalyticsActionTypes.DOCUMENT_RISK_TOLERANCE_PAGINATION_PREVIOUS, documentRiskTolerancePaginationPrevious);
}

export function* documentRiskToleranceTableConfigUpdated() {
    const tileInView: DocumentAnalyticsChart = yield select(getTileInView);
    const companyEntityId: number | null = yield select(getSelectedMyCompanyEntity);
    const isPreExecution = tileInView === DocumentAnalyticsChart.PRE_EXECUTION_DOCUMENT_RISK_TOLERANCE;
    const isEntityRiskToleranceView = tileInView === DocumentAnalyticsChart.ENTITY_DOCUMENT_RISK_TOLERANCE;
    const entityId = !isNull(companyEntityId) && isEntityRiskToleranceView ? companyEntityId : undefined;
    yield put(fetchPaginatedDocumentsRiskToleranceStarted(1, entityId, isPreExecution));
}

function* documentRiskToleranceTableConfigUpdatedWatcher() {
    yield takeEvery([DocumentAnalyticsActionTypes.SET_DOCUMENT_RISK_TOLERANCE_TABLE_FILTERS, DocumentAnalyticsActionTypes.CLEAR_DOCUMENT_RISK_TOLERANCE_TABLE_FILTERS, DocumentAnalyticsActionTypes.SET_DOCUMENT_RISK_TOLERANCE_TABLE_COLUMN_SORT, DocumentAnalyticsActionTypes.SET_DOCUMENT_RISK_TOLERANCE_PAGE_SIZE, DocumentAnalyticsActionTypes.TOGGLE_SHOW_OVERRIDE_RISK_RATING], documentRiskToleranceTableConfigUpdated);
}

export function* documentRiskToleranceTableFiltersUpdated() {
    const tileInView: DocumentAnalyticsChart = yield select(getTileInView);
    const companyEntityId: number | null = yield select(getSelectedMyCompanyEntity);
    const isPreExecution = tileInView === DocumentAnalyticsChart.PRE_EXECUTION_DOCUMENT_RISK_TOLERANCE;
    const isEntityRiskToleranceView = tileInView === DocumentAnalyticsChart.ENTITY_DOCUMENT_RISK_TOLERANCE;
    const entityId = !isNull(companyEntityId) && isEntityRiskToleranceView ? companyEntityId : undefined;
    if (isPreExecution) {
        yield put(fetchAllPreExecutionDocumentsRiskToleranceStarted());
    } else if (isEntityRiskToleranceView && !isUndefined(entityId)) {
        yield put(fetchAllEntityDocumentsRiskToleranceStarted(entityId));
    } else {
        yield put(fetchAllDocumentsRiskToleranceStarted());
    }
}

function* documentRiskToleranceTableFiltersUpdatedWatcher() {
    yield takeEvery([DocumentAnalyticsActionTypes.SET_DOCUMENT_RISK_TOLERANCE_TABLE_FILTERS, DocumentAnalyticsActionTypes.CLEAR_DOCUMENT_RISK_TOLERANCE_TABLE_FILTERS], documentRiskToleranceTableFiltersUpdated);
}

// Dataset Field Table Pagination Sagas

export function* datasetFieldRiskTolerancePaginationNext() {
    const pageNumber: number = yield select(getRiskToleranceDatasetFieldPageNumber);
    const tileInView: DocumentAnalyticsChart = yield select(getTileInView);
    const companyEntityId: number | null = yield select(getSelectedMyCompanyEntity);
    const isPreExecution = tileInView === DocumentAnalyticsChart.PRE_EXECUTION_DOCUMENT_RISK_TOLERANCE;
    const isEntityRiskToleranceView = tileInView === DocumentAnalyticsChart.ENTITY_DOCUMENT_RISK_TOLERANCE;
    const entityId = !isNull(companyEntityId) && isEntityRiskToleranceView ? companyEntityId : undefined;
    yield put(fetchDatasetFieldRiskToleranceStarted(pageNumber + 1, entityId, isPreExecution));
}

function* datasetFieldRiskTolerancePaginationNextWatcher() {
    yield takeLatest(DocumentAnalyticsActionTypes.DATASET_FIELD_RISK_TOLERANCE_PAGINATION_NEXT, datasetFieldRiskTolerancePaginationNext);
}

export function* datasetFieldRiskTolerancePaginationPrevious() {
    const pageNumber: number = yield select(getRiskToleranceDatasetFieldPageNumber);
    const tileInView: DocumentAnalyticsChart = yield select(getTileInView);
    const companyEntityId: number | null = yield select(getSelectedMyCompanyEntity);
    const isPreExecution = tileInView === DocumentAnalyticsChart.PRE_EXECUTION_DOCUMENT_RISK_TOLERANCE;
    const isEntityRiskToleranceView = tileInView === DocumentAnalyticsChart.ENTITY_DOCUMENT_RISK_TOLERANCE;
    const entityId = !isNull(companyEntityId) && isEntityRiskToleranceView ? companyEntityId : undefined;
    yield put(fetchDatasetFieldRiskToleranceStarted(pageNumber - 1, entityId, isPreExecution));
}

function* datasetFieldRiskTolerancePaginationPreviousWatcher() {
    yield takeLatest(DocumentAnalyticsActionTypes.DATASET_FIELD_RISK_TOLERANCE_PAGINATION_PREVIOUS, datasetFieldRiskTolerancePaginationPrevious);
}

export function* datasetFieldRiskToleranceTableConfigUpdated() {
    const tileInView: DocumentAnalyticsChart = yield select(getTileInView);
    const companyEntityId: number | null = yield select(getSelectedMyCompanyEntity);
    const isPreExecution = tileInView === DocumentAnalyticsChart.PRE_EXECUTION_DOCUMENT_RISK_TOLERANCE;
    const isEntityRiskToleranceView = tileInView === DocumentAnalyticsChart.ENTITY_DOCUMENT_RISK_TOLERANCE;
    const entityId = !isNull(companyEntityId) && isEntityRiskToleranceView ? companyEntityId : undefined;
    yield put(fetchDatasetFieldRiskToleranceStarted(1, entityId, isPreExecution));
}

function* datasetFieldRiskToleranceTableConfigUpdatedWatcher() {
    yield takeEvery([DocumentAnalyticsActionTypes.SET_DATASET_FIELD_RISK_TOLERANCE_TABLE_FILTERS, DocumentAnalyticsActionTypes.CLEAR_DATASET_FIELD_RISK_TOLERANCE_TABLE_FILTERS, DocumentAnalyticsActionTypes.SET_DATASET_FIELD_RISK_TOLERANCE_TABLE_COLUMN_SORT, DocumentAnalyticsActionTypes.SET_DATASET_FIELD_RISK_TOLERANCE_PAGE_SIZE, DocumentAnalyticsActionTypes.TOGGLE_SHOW_OVERRIDE_RISK_RATING], datasetFieldRiskToleranceTableConfigUpdated);
}

export function* attemptRecalculateDocumentRiskTolerance() {
    try {
        yield call(recalculateClientDocumentRiskTolerance);
        yield put(recalculateMyDocumentRiskToleranceSuccessful());
    } catch (e) {
        yield put(recalculateMyDocumentRiskToleranceFailed((e as Error).message));
    }
}

function* recalculateDocumentRiskToleranceWatcher() {
    yield takeLeading(DocumentAnalyticsActionTypes.RECALCULATE_MY_DOCUMENT_RISK_TOLERANCE_STARTED, attemptRecalculateDocumentRiskTolerance);
}

export function* updateDocumentRiskToleranceCharts() {
    yield put(fetchAllDocumentsRiskToleranceStarted());
    const companyEntityId: number | null = yield select(getSelectedMyCompanyEntity);
    if (companyEntityId) {
        yield put(fetchAllEntityDocumentsRiskToleranceStarted(companyEntityId));
    }
    yield put(fetchAllPreExecutionDocumentsRiskToleranceStarted());
}

function* documentRiskToleranceCompletedWatcher() {
    yield takeLeading(DocumentAnalyticsActionTypes.SET_RISK_TOLERANCE_COMPLETED, updateDocumentRiskToleranceCharts);
}

export function* documentAnalyticsSaga() {
    yield all([
        fork(setTileInViewWatcher),
        fork(fetchDocumentSpreadWatcher),
        fork(fetchEntitySpreadWatcher),
        fork(fetchEntityJurisdictionWatcher),
        fork(fetchJurisdictionDocumentsWatcher),
        fork(jurisdictionDocumentsSelectPageWatcher),
        fork(attemptFetchAvailableAnalyticsEntitiesWatcher),
        fork(setSelectedCompanyWatcher),
        fork(fetchDocumentRiskToleranceWatcher),
        fork(fetchAllDocumentsRiskToleranceWatcher),
        fork(fetchPaginatedDocumentsRiskToleranceWatcher),
        fork(documentRiskTolerancePaginationNextWatcher),
        fork(documentRiskTolerancePaginationPreviousWatcher),
        fork(documentRiskToleranceTableConfigUpdatedWatcher),
        fork(fetchAllRiskToleranceWatcher),
        fork(fetchPaginatedDatasetFieldRiskToleranceWatcher),
        fork(datasetFieldRiskTolerancePaginationNextWatcher),
        fork(datasetFieldRiskTolerancePaginationPreviousWatcher),
        fork(datasetFieldRiskToleranceTableConfigUpdatedWatcher),
        fork(goToDocumentInstance),
        fork(fetchAllEntityDocumentsRiskToleranceWatcher),
        fork(fetchAllPreExecutionDocumentsRiskToleranceWatcher),
        fork(recalculateDocumentRiskToleranceWatcher),
        fork(documentRiskToleranceCompletedWatcher),
        fork(documentRiskToleranceTableFiltersUpdatedWatcher)
    ]);
}
